import { create } from 'zustand';
import { api } from '@/shared/api/client';
import type { PermLevel } from '@/shared/permissions/catalog';
import { NODES } from '@/shared/permissions/catalog';

// Effective-permissions store. Hydrated once at login (from
// /api/v1/permissions/effective) and re-hydrated whenever the user
// switches companies. Reads are cheap selectors; writes only happen
// in hydrate().
//
// Pre-hydrate state defaults to "everything write" so the UI never
// flashes a hidden state to a freshly-logged-in user during the
// first round-trip. Once hydration lands the real grid takes over.

type State = {
  effective: Record<string, PermLevel>;
  hydrated: boolean;
  hydrate: () => Promise<void>;
  reset: () => void;
};

function defaultMap(level: PermLevel): Record<string, PermLevel> {
  const out: Record<string, PermLevel> = {};
  for (const n of NODES) out[n.path] = level;
  return out;
}

const PERMISSIVE = defaultMap('write');

export const usePermissionsStore = create<State>((set) => ({
  effective: PERMISSIVE,
  hydrated: false,
  hydrate: async () => {
    try {
      const data = await api.get<Record<string, PermLevel>>('/permissions/effective');
      // Merge over the permissive default so any node missing in the
      // server response (e.g. mid-deploy schema mismatch) stays usable.
      set({ effective: { ...PERMISSIVE, ...data }, hydrated: true });
    } catch {
      // On 401 / network error keep the permissive default. The user
      // will get a fresh fetch on the next login round; we don't want
      // to lock them out of UI based on a transient failure.
      set({ hydrated: true });
    }
  },
  reset: () => set({ effective: PERMISSIVE, hydrated: false }),
}));
